import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { auth } from '../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaCheckCircle, FaStar, FaArrowRight } from 'react-icons/fa';
import { trackGA4Event, getGAClientId } from '../utils/GA4Analytics';
import { trackFunnelEvent } from '../utils/AnalyticsService';

export default function EbookLanding() {
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const [user, setUser] = useState(null);
    const [activePreviewTab, setActivePreviewTab] = useState(0);
    const [showStickyFooter, setShowStickyFooter] = useState(false);
    const [currency, setCurrency] = useState('INR');

    const locale = sessionStorage.getItem('versant_locale') || 'en';

    const translations = {
        en: {
            metaTitle: "Versant Test Practice Guide (2026) | 20 Mock Tests & AI Templates",
            metaDesc: "Practice the Versant English Test with 20 mock tests, Repeat Sentence templates, Story Retelling strategies, and AI speaking tips. Instant PDF download.",
            boostTag: "🚀 Boost Your Score Immediately",
            title: "Versant Test Practice Guide",
            subtitle: "Practice the Versant English Test with 20 Full Mock Tests, Proven Templates & AI Scoring Strategies",
            desc: "Improve your Versant Test score with structured practice, AI-focused speaking strategies, Repeat Sentence templates, Story Retelling frameworks, Listening practice, and 20 full-length practice sets trusted by 5,000+ learners.",
            features: [
                "Master Repeat Sentence & Story Retelling speaking templates.",
                "Practice Sentence Builds & Conversations with 2026 mock tests.",
                "Acoustic adjustment guidelines to ensure the AI microphone scores you correctly.",
                "Perfect score model responses for all 20 Mock Test questions."
            ],
            buyBtn: "Get Instant Access",
            buyBtnProc: "Launching Gateway...",
            regPrice: "Regular Price",
            onetime: "One-time",
            badgeTxt1: "⚡ Secure Razorpay Checkout",
            badgeTxt2: "📥 Instant PDF Download",
            badgeTxt3: "🔄 Lifetime Access & Updates",
            badgeTxt4: "🔥 60% Off Offer Ends August 3",
            trustHeader: "Trusted by 5,000+ Successful Candidates",
            trustDesc: "Scoring 68+ on average globally in corporate English evaluations.",
            designedFor: "Designed For:",
            insideTitle: "What's Inside the Guide?",
            insideDesc: "Get complete blueprints to hack the evaluation algorithm and ace your interview.",
            insideItems: [
                { title: "Story Retelling Templates", desc: "The exact grammatical frameworks to structure your response, ensuring maximum points for fluency even if you miss details.", icon: "📝" },
                { title: "AI Mic Scoring Optimization", desc: "Secrets on pitch modulation, volume adjustment, and mic placement to prevent the AI grading software from penalizing your audio.", icon: "🎙️" },
                { title: "20 Mock Test Answer Key", desc: "Sample 80-GSE responses for all Speaking, Listening, Reading, and Writing sections in our mock tests.", icon: "💡" },
                { title: "MNC Screening Cheat Sheets", desc: "Direct lists of high-frequency words, phrases, and structures preferred by screening systems at Amazon, Deloitte, and Cognizant.", icon: "🏢" }
            ],
            diffTitle: "Why This Guide is Different",
            diffDesc: "Compare our structured, algorithm-focused practice system with generic preparation resources.",
            diffHeaders: ["Key Feature", "Random YouTube & Free Tips", "Versant Test Practice Guide"],
            diffRows: [
                { name: "Learning Path", other: "Scattered videos and conflicting advice", our: "Structured, step-by-step 14-day study roadmap" },
                { name: "Ready Templates", other: "No ready frameworks for Story Retelling", our: "Proven scoring templates that boost fluency marks" },
                { name: "Practice Questions", other: "Outdated sentences and audio prompt leaks", our: "20 full mock tests matching the 2026 pattern" },
                { name: "Mic & AI Calibration", other: "Vague speaking tips ('sound confident')", our: "Exact acoustic guidelines to prevent microphone scoring bugs" }
            ],
            previewTitle: "Sneak Peek: Inside the Ebook",
            previewDesc: "Get a glimpse of the actual content, strategies, and templates that will help you clear the test. Click on the tabs below to read sample pages.",
            previewTabs: [
                "📄 Page 12: Story Retelling Hack",
                "🎙️ Page 27: Voice Calibration Guide",
                "💡 Page 45: High-Score Model Response"
            ],
            ch1Title: "CHAPTER 2: STORY RETELLING",
            ch1Sub: "Rule #3: The AI Fluency Template",
            ch1P: "The AI voice grading system measures speech rate, pause counts, and rhythm. It does not grade the accuracy of your memory, but how naturally you speak. Use the exact structure below:",
            ch1Tip: "💡 Insider Tip: If you forget details, insert generic synonyms relating to the topic rather than pausing or saying \"Umm\". The AI system counts hesitation as a major deduction!",
            ch2Title: "CHAPTER 4: ACOUSTIC CALIBRATION",
            ch2Sub: "Secret #7: AI Audio Frequency Tuning",
            ch2P: "Many test-takers score poorly not because of bad English, but due to bad acoustic signals. The voice evaluation software uses voice spectrum analysis to decode phonemes.",
            ch2MicTitle: "🎙️ Microphone Placement",
            ch2MicDesc: "Do not place the microphone directly in front of your mouth. Heavy breath pops (plosives like 'P' and 'B') distort the frequency curves. Keep the microphone two fingers away from your chin or nose level.",
            ch2PitchTitle: "🗣️ Pitch & Frequency",
            ch2PitchDesc: "Keep your voice pitch flat and stable. High variance in tone makes the AI confuse your voice with background noise. Maintain a stable frequency range of 110Hz to 160Hz.",
            ch2Tip: "💡 Insider Tip: Speak slowly but confidently. High speed causes slurred speech boundaries, which the scoring software registers as grammatical mistakes.",
            ch3Title: "CHAPTER 6: MODEL TEST KEY ANSWERS",
            ch3Sub: "Mock Test Part F: Sentence Passages (Score 80 GSE)",
            ch3P: "Below is a sample question from Practice Set 4 and the model high-scoring answer that satisfies all parameters for vocabulary, grammar, and voice flow.",
            ch3QTitle: "Prompt Question / Sentence Build task:",
            ch3ATitle: "Correct Model Response (Score 80 GSE):",
            ch3Tip: "💡 Insider Tip: For sentence builds, focus on immediate subject-verb agreement first. The scoring software tracks correct structural syntax first and semantic context second.",
            footerBar: "Versant Test Mastery Guide",
            secPreview: "Secure PDF Preview",
            reviewsTitle: "Successful Candidates Say It Best",
            reviews: [
                { name: "Harish M.", score: "Rejected previously, scored 71 after guide", review: "The template for Story Retelling inside this PDF is absolute gold. I was using too many filler words. Once I followed the guide, I cleared Amazon's interview easily." },
                { name: "Pooja Hegde", score: "Scored 69 on Deloitte Cutoff", review: "Acoustic guide changed everything. It explained where to place the mic and how to modulate voice. Highly recommended if you fail the audio scoring." },
                { name: "Vikram Sen", score: "Cleared mock tests in 4 days", review: "Very straightforward guide without fluff. Sample responses for all sentence builds helped me score 75. Saved me a lot of stress!" }
            ],
            compTitle: "Complete Preparation Resources",
            compDesc: "Get all the essential tools and knowledge to master your corporate English screenings.",
            compItems: [
                { title: "Amazon Versant Test Prep", desc: "Amazon uses the Versant test to screen customer support, operations, and technical roles. Our eBook covers the exact templates and keywords needed to satisfy Amazon's strict automated hiring algorithms, helping you clear the initial HR round safely." },
                { title: "Deloitte Voice Screening hacks", desc: "Deloitte Consulting screenings demand high scores in grammar, fluency, and pronunciation. The guide offers exact phonetic correction tips and pitch parameters required to score above their standard corporate recruitment cutoff thresholds." },
                { title: "Voice AI Scoring Algorithms", desc: "Learn how Pearson's automated evaluation engines process voice patterns. We explain the science of voice spectrographs, plosives, and how to calibrate your pronunciation flow to ensure the AI grades your responses accurately." }
            ],
            faqTitle: "Frequently Asked Questions",
            faqSub: "Everything you need to know about the Versant Test Mastery Guide.",
            faqs: [
                { q: "How will I receive the ebook?", a: "Immediately after your payment is verified, you will return to the Thank You page where you will get a secure direct 'Download Ebook' button. You can also set a password on that page to save it to your user dashboard forever." },
                { q: "Do I get updates when the 2026 pattern changes?", a: "Yes! All future editions and vocabulary updates of this guide are completely free. You can re-download the latest version from your dashboard at any time." },
                { q: "Will this guide help me clear Amazon or Deloitte cutoffs?", a: "Absolutely. The templates and speaking guidelines are designed specifically to target corporate screening parameters, which help candidates clear automated voice screening cutoffs." },
                { q: "Can I read this ebook on my mobile phone?", a: "Yes. The PDF is fully responsive and beautifully formatted for mobiles, tablets, laptops, and Kindle readers." },
                { q: "Is this suitable for beginners?", a: "Yes. It starts from absolute spelling and pronunciation basics, moving systematically into speaking templates, acoustic micro-tuning, and mock tests." },
                { q: "Can I get a refund if I'm not satisfied?", a: "Yes, we offer a 100% satisfaction guarantee. If you feel the practice tests did not help your preparation, contact our support within 7 days for a full refund." }
            ]
        },
        es: {
            metaTitle: "Guía de práctica de la prueba Versant (2026) | 20 simulacros y plantillas de IA",
            metaDesc: "Practique el examen de inglés Versant con 20 simulacros, plantillas de Repeat Sentence y consejos de oratoria por IA. Descarga instantánea en PDF.",
            boostTag: "🚀 Mejora tu puntuación inmediatamente",
            title: "Guía de Práctica del Examen Versant",
            subtitle: "Practica el examen de inglés Versant con 20 simulacros completos, plantillas comprobadas y estrategias de calificación de IA",
            desc: "Mejora tu puntuación en el examen Versant con práctica estructurada, estrategias de expresión oral centradas en IA, plantillas de Repetición de Oraciones, marcos de Reconstrucción de Historias, práctica de Escucha y 20 simulacros completos utilizados por más de 5,000 estudiantes.",
            features: [
                "Domina plantillas de expresión oral para Repetir Oraciones y Reconstruir Historias.",
                "Practica la construcción de oraciones y conversaciones con simulacros de 2026.",
                "Pautas de ajuste acústico para garantizar que el micrófono de la IA te califique correctamente.",
                "Respuestas de muestra de puntuación perfecta para las 20 preguntas del examen de simulacro."
            ],
            buyBtn: "Obtener Acceso Instantáneo",
            buyBtnProc: "Iniciando pasarela...",
            regPrice: "Precio Regular",
            onetime: "Pago Único",
            badgeTxt1: "⚡ Pago Seguro con Razorpay",
            badgeTxt2: "📥 Descarga Inmediata de PDF",
            badgeTxt3: "🔄 Acceso y Actualizaciones de por Vida",
            badgeTxt4: "🔥 60% de Descuento Especial",
            trustHeader: "Con la confianza de más de 5,000 candidatos exitosos",
            trustDesc: "Puntuación de 68+ en promedio a nivel mundial en evaluaciones corporativas de inglés.",
            designedFor: "Diseñado para:",
            insideTitle: "¿Qué hay dentro de la guía?",
            insideDesc: "Obtén planos completos para descifrar el algoritmo de evaluación y aprobar tu entrevista.",
            insideItems: [
                { title: "Plantillas de Reconstrucción", desc: "Los marcos gramaticales exactos para estructurar tu respuesta, garantizando los máximos puntos por fluidez incluso si olvidas detalles.", icon: "📝" },
                { title: "Optimización de Micrófono IA", desc: "Secretos sobre modulación de tono, ajuste de volumen y colocación del micrófono para evitar penalizaciones por mala acústica.", icon: "🎙️" },
                { title: "Respuestas de los 20 Simulacros", desc: "Respuestas modelo de nivel 80-GSE para todas las secciones de expresión oral, escucha, lectura y escritura.", icon: "💡" },
                { title: "Guías para Entrevistas MNC", desc: "Listas directas de palabras, frases y estructuras de alta frecuencia preferidas por los reclutadores en Amazon, Deloitte y Cognizant.", icon: "🏢" }
            ],
            diffTitle: "Por qué esta guía es diferente",
            diffDesc: "Compara nuestro sistema de práctica estructurado centrado en algoritmos con los recursos genéricos.",
            diffHeaders: ["Característica clave", "YouTube aleatorio y consejos gratis", "Guía de práctica de Versant Test"],
            diffRows: [
                { name: "Ruta de aprendizaje", other: "Videos dispersos y consejos contradictorios", our: "Hoja de ruta de estudio de 14 días estructurada paso a paso" },
                { name: "Plantillas listas", other: "Sin marcos listos para la reconstrucción de historias", our: "Plantillas de puntuación probadas que mejoran la fluidez" },
                { name: "Preguntas de práctica", other: "Oraciones obsoletas y preguntas antiguas", our: "20 simulacros completos que coinciden con el patrón de 2026" },
                { name: "Calibración de Micrófono e IA", other: "Consejos vagos sobre hablar ('suena seguro')", our: "Exact acoustic guidelines to prevent microphone scoring bugs" }
            ],
            previewTitle: "Vista previa: Dentro del eBook",
            previewDesc: "Obtén un vistazo del contenido real, las estrategias y las plantillas que te ayudarán a aprobar el examen. Haz clic en las pestañas para ver las páginas de muestra.",
            previewTabs: [
                "📄 Página 12: Truco de Reconstrucción",
                "🎙️ Página 27: Guía de Calibración de Voz",
                "💡 Página 45: Respuesta Modelo de Puntuación Alta"
            ],
            ch1Title: "CAPÍTULO 2: RECONSTRUCCIÓN DE HISTORIAS",
            ch1Sub: "Regla #3: La plantilla de fluidez de la IA",
            ch1P: "El sistema de calificación de voz por IA mide la velocidad del habla, el conteo de pausas y el ritmo. No califica la precisión de tu memoria, sino la naturalidad con la que hablas. Utiliza la estructura a continuación:",
            ch1Tip: "💡 Consejo experto: Si olvidas detalles, inserta sinónimos generales relacionados con el tema en lugar de hacer pausas o decir \"Umm\". ¡El sistema de IA cuenta la vacilación como una penalización mayor!",
            ch2Title: "CAPÍTULO 4: CALIBRACIÓN ACÚSTICA",
            ch2Sub: "Secreto #7: Sintonización de frecuencia de audio IA",
            ch2P: "Muchos candidatos obtienen puntuaciones bajas no por un mal inglés, sino por malas señales acústicas. El software de evaluación utiliza el análisis del espectro de voz para decodificar los fonemas.",
            ch2MicTitle: "🎙️ Colocación del Micrófono",
            ch2MicDesc: "No coloques el micrófono directamente frente a tu boca. Los soplidos fuertes de aire (plosivas como 'P' y 'B') distorsionan la frecuencia. Mantén el micrófono a dos dedos de distancia de tu barbilla o nivel de la nariz.",
            ch2PitchTitle: "🗣️ Tono y Frecuencia",
            ch2PitchDesc: "Mantén el tono de tu voz plano y estable. Una alta variación de tono hace que la IA confunda tu voz con el ruido de fondo. Mantén un rango de frecuencia estable de 110Hz a 160Hz.",
            ch2Tip: "💡 Consejo experto: Habla despacio pero con confianza. La alta velocidad provoca límites de voz confusos, que el software registra como errores gramaticales.",
            ch3Title: "CAPÍTULO 6: RESPUESTAS DE SIMULACROS",
            ch3Sub: "Simulacro Parte F: Construcción de Oraciones (Puntuación 80 GSE)",
            ch3P: "A continuación se muestra una pregunta de muestra del Conjunto de Práctica 4 y la respuesta modelo de puntuación alta que cumple con todos los parámetros de vocabulario, gramática y flujo.",
            ch3QTitle: "Pregunta de muestra / Tarea de construcción de oraciones:",
            ch3ATitle: "Respuesta modelo correcta (Puntuación 80 GSE):",
            ch3Tip: "💡 Consejo experto: Para la construcción de oraciones, concéntrate primero en la concordancia inmediata sujeto-verbo. El software realiza el seguimiento de la sintaxis estructural primero y el contexto semántico después.",
            footerBar: "Guía de preparación del examen Versant",
            secPreview: "Vista previa segura en PDF",
            reviewsTitle: "Los candidatos exitosos lo dicen mejor",
            reviews: [
                { name: "Harish M.", score: "Rechazado previamente, obtuvo 71 tras la guía", review: "La plantilla para Story Retelling dentro de este PDF es oro puro. Estaba usando demasiadas palabras de relleno. Una vez que seguí la guía, pasé la entrevista de Amazon fácilmente." },
                { name: "Pooja Hegde", score: "Obtuvo 69 en el filtro de Deloitte", review: "La guía acústica lo cambió todo. Explicaba dónde colocar el micrófono y cómo modular la voz. Muy recomendado si fallas en la puntuación de audio." },
                { name: "Vikram Sen", score: "Completó simulacros en 4 días", review: "Una guía muy directa y sin rodeos. Las respuestas de muestra para todas las construcciones de oraciones me ayudaron a obtener 75. ¡Me ahorró mucho estrés!" }
            ],
            compTitle: "Recursos de preparación completos",
            compDesc: "Obtén todas las herramientas y conocimientos esenciales para dominar tus filtros de inglés corporativos.",
            compItems: [
                { title: "Preparación para Amazon Versant", desc: "Amazon utiliza el examen Versant para puestos de atención al cliente, operaciones y técnicos. Nuestra guía cubre las plantillas exactas para superar sus filtros automatizados." },
                { title: "Hack de voz para Deloitte", desc: "Las evaluaciones de Deloitte exigen puntuaciones antes de calificar. Ofrece consejos de corrección fonética exactos." },
                { title: "Algoritmos de Calificación IA", desc: "Aprende cómo procesan la voz los motores automatizados de Pearson. Explicamos la ciencia de los espectrógrafos para garantizar una calificación precisa." }
            ],
            faqTitle: "Preguntas Frecuentes",
            faqSub: "Todo lo que necesitas saber sobre la Guía de preparación Versant.",
            faqs: [
                { q: "¿Cómo recibiré el eBook?", a: "Inmediatamente después de verificar el pago, volverás a la página de agradecimiento donde obtendrás un botón de descarga directa del eBook. También puedes configurar una contraseña para guardarlo en tu panel." },
                { q: "¿Recibo actualizaciones si cambia el patrón de 2026?", a: "¡Sí! Todas las futuras ediciones y actualizaciones de vocabulario de esta guía son completamente gratuitas. Puedes volver a descargarlas desde tu panel en cualquier momento." },
                { q: "¿Me ayudará esta guía a superar los filtros de Amazon o Deloitte?", a: "Absolutamente. Las plantillas y pautas de expresión oral están diseñadas específicamente para cumplir con los parámetros de los sistemas automáticos de filtrado de voz corporativos." },
                { q: "¿Puedo leer este eBook en mi teléfono móvil?", a: "Sí. El PDF es totalmente responsive y está formateado para pantallas móviles, tablets, ordenadores portátiles y lectores Kindle." },
                { q: "¿Es adecuado para principiantes?", a: "Sí. Comienza desde los conceptos básicos de ortografía y pronunciación, avanzando hacia plantillas de expresión oral, microajustes acústicos y simulacros." },
                { q: "¿Puedo obtener un reembolso si no estoy satisfecho?", a: "Sí, ofrecemos una garantía de satisfacción del 100%. Si crees que las pruebas no te ayudaron, ponte en contacto con nuestro equipo dentro de los 7 días para un reembolso completo." }
            ]
        },
        pt: {
            metaTitle: "Guia de Prática do Teste Versant (2026) | 20 Simulados e Modelos de IA",
            metaDesc: "Pratique o Teste Versant com 20 simulados, modelos de Repeat Sentence e dicas de oratória por IA. Download instantâneo de PDF.",
            boostTag: "🚀 Melhore sua pontuação imediatamente",
            title: "Guia de Prática do Teste Versant",
            subtitle: "Pratique o Teste de Inglês Versant com 20 Simulados Completos, Modelos Comprovados e Estratégias de Avaliação de IA",
            desc: "Melhore a sua pontuação no Teste Versant com prática estruturada, estratégias de fala focadas na IA, modelos de Repeat Sentence, estruturas de Story Retelling, prática de Listening e 20 simulados completos de confiança de mais de 5.000 alunos.",
            features: [
                "Domine os modelos de fala para Repeat Sentence e Story Retelling.",
                "Pratique a construção de frases e conversas com simulados de 2026.",
                "Diretrizes de calibração acústica para garantir que o microfone da IA o avalie corretamente.",
                "Respostas perfeitas de exemplo para todas as 20 perguntas dos testes simulados."
            ],
            buyBtn: "Obter Acesso Instantâneo",
            buyBtnProc: "Iniciando gateway...",
            regPrice: "Preço Regular",
            onetime: "Pagamento Único",
            badgeTxt1: "⚡ Checkout Seguro Razorpay",
            badgeTxt2: "📥 Download Imediato em PDF",
            badgeTxt3: "🔄 Acesso e Atualizações Vitalícias",
            badgeTxt4: "🔥 60% de Desconto Especial",
            trustHeader: "De confiança de mais de 5.000 candidatos de sucesso",
            trustDesc: "Média de 68+ pontos globalmente em avaliações corporativas de inglês.",
            designedFor: "Projetado Para:",
            insideTitle: "O Que Há Dentro do Guia?",
            insideDesc: "Obtenha planos de ação completos para hackear o algoritmo de avaliação e passar na entrevista.",
            insideItems: [
                { title: "Modelos de Story Retelling", desc: "As estruturas gramaticais exatas para organizar sua resposta, garantindo nota máxima em fluência mesmo se esquecer detalhes.", icon: "📝" },
                { title: "Calibração de Microfone para IA", desc: "Segredos de modulação de voz, volume e posicionamento do microfone para evitar punições por má qualidade de captação.", icon: "🎙️" },
                { title: "Gabarito dos 20 Simulados", desc: "Respostas modelo de pontuação 80-GSE para todas as seções de conversação, escuta, leitura e escrita.", icon: "💡" },
                { title: "Guias de Entrevista MNC", desc: "Listas de palavras, expressões e estruturas mais valorizadas pelos algoritmos de triagem na Amazon, Deloitte e Cognizant.", icon: "🏢" }
            ],
            diffTitle: "Por Que Este Guia é Diferente",
            diffDesc: "Compare o nosso método de simulados focado nos algoritmos com dicas soltas na internet.",
            diffHeaders: ["Recurso Principal", "Dicas Gratuitas de YouTube", "Guia de Prática do Teste Versant"],
            diffRows: [
                { name: "Caminho de Aprendizado", other: "Vídeos dispersos e conselhos contraditórios", our: "Cronograma de estudos de 14 dias estruturado passo a passo" },
                { name: "Modelos Prontos", other: "Sem modelos prontos para responder Story Retelling", our: "Modelos comprovados que maximizam a nota de fluência" },
                { name: "Questões Práticas", other: "Frases antigas e arquivos de áudio desatualizados", our: "20 simulados completos baseados nos padrões de 2026" },
                { name: "Calibração de Microfone", other: "Dicas vagas de fala ('fale com confiança')", our: "Diretrizes acústicas exatas para evitar erros de leitura do microfone" }
            ],
            previewTitle: "Espreite Por Dentro do eBook",
            previewDesc: "Dê uma olhada no conteúdo real, estratégias e modelos que ajudarão você a passar. Clique nas guias para ver as páginas de exemplo.",
            previewTabs: [
                "📄 Página 12: Dica de Story Retelling",
                "🎙️ Página 27: Guia de Calibração de Voz",
                "💡 Página 45: Resposta Modelo de Alta Pontuação"
            ],
            ch1Title: "CAPÍTULO 2: RECONSTRUÇÃO DE HISTÓRIAS",
            ch1Sub: "Regla #3: O Modelo de Fluência da IA",
            ch1P: "O sistema de avaliação de voz avalia velocidade da fala, pausas e ritmo. Ele não mede a memória perfeita, mas sim o fluxo de fala natural. Use a estrutura abaixo:",
            ch1Tip: "💡 Dica de Especialista: Se esquecer detalhes, insira sinônimos gerais sobre o tema em vez de pausar ou dizer \"Umm\". A hesitação diminui muito a sua nota!",
            ch2Title: "CAPÍTULO 4: CALIBRAÇÃO ACÚSTICA",
            ch2Sub: "Segredo #7: Ajuste de Frequência de Áudio para IA",
            ch2P: "Muitos candidatos falham não por causa do inglês ruim, mas por sinais acústicos deficientes. O avaliador usa análise de espectro de áudio para reconhecer fonemas.",
            ch2MicTitle: "🎙️ Posicionamento do Microfone",
            ch2MicDesc: "Não coloque o microfone logo em frente à boca. Ruídos de respiração forte (plosivas como 'P' e 'B') distorcem as curvas de som. Mantenha a dois dedos do queixo ou nariz.",
            ch2PitchTitle: "🗣️ Tom e Frequência de Voz",
            ch2PitchDesc: "Mantenha o tom estável. Grandes oscilações de voz fazem a IA confundir sua fala com ruído. Mantenha a frequência na faixa de 110Hz a 160Hz.",
            ch2Tip: "💡 Dica de Especialista: Fale devagar, mas com firmeza. Falar rápido demais causa atropelo de fonemas, o que a IA entende como erros de gramática.",
            ch3Title: "CAPÍTULO 6: GABARITOS DE RESPOSTAS",
            ch3Sub: "Simulado Parte F: Construção de Frases (Nota 80 GSE)",
            ch3P: "Veja abaixo uma questão do Simulado 4 e a resposta de exemplo recomendada para atender a todos os critérios de vocabulário, gramática e dicção.",
            ch3QTitle: "Enunciado / Tarefa de Organização de Frase:",
            ch3ATitle: "Resposta Modelo Correta (Nota 80 GSE):",
            ch3Tip: "💡 Dica de Especialista: Foco inicial na concordância sujeito-verbo. A IA valida a sintaxe estrutural primeiro, e o contexto semântico em segundo.",
            footerBar: "Guia de Preparação Versant",
            secPreview: "Visualização Segura do PDF",
            reviewsTitle: "Veja o Que Dizem Nossos Alunos",
            reviews: [
                { name: "Harish M.", score: "Reprovado antes, tirou 71 com o guia", review: "O modelo de Story Retelling neste PDF é espetacular. Eu usava muitos marcadores de dúvida. Após ler o guia, passei na entrevista da Amazon com facilidade." },
                { name: "Pooja Hegde", score: "Nota 69 na Deloitte", review: "O guia acústico mudou tudo. Ele explica onde posicionar o microfone e como modular o tom da voz. Essencial para quem falha no teste de áudio." },
                { name: "Vikram Sen", score: "Estudou os simulados em 4 dias", review: "Direto ao ponto, sem enrolação. Os exemplos práticos de organização de frases me ajudaram a tirar 75. Excelente investimento!" }
            ],
            compTitle: "Conteúdo Completo de Preparação",
            compDesc: "Acesse as informações certas para superar os processos de recrutamento das multinacionais.",
            compItems: [
                { title: "Preparação para Amazon Versant", desc: "A Amazon usa o teste Versant para triagem de vagas operacionais e técnicas. Nosso eBook cobre as palavras-chave necessárias para aprovação." },
                { title: "Segredos de Voz para a Deloitte", desc: "Avaliações para a Deloitte exigem alta fluência, pronúncia e gramática. O guia traz dicas de correção fonética detalhadas." },
                { title: "Algoritmos de IA de Voz", desc: "Entenda como o avaliador da Pearson interpreta as frequências da fala. Explicamos o funcionamento de espectrógrafos para evitar erros no teste." }
            ],
            faqTitle: "Perguntas Frequentes",
            faqSub: "Tudo o que você precisa saber sobre o Guia Versant.",
            faqs: [
                { q: "Como recebo o eBook?", a: "Imediatamente após a confirmação do pagamento, você voltará para a página de Obrigado, onde poderá baixar o PDF diretamente. Também poderá criar uma senha para salvá-lo no seu painel." },
                { q: "Recebo atualizações se o teste de 2026 mudar?", a: "Sim! Todas as atualizações futuras e adições de vocabulário deste guia são gratuitas. Você pode baixar a versão atualizada no seu painel quando quiser." },
                { q: "Esse guia funciona para a Amazon e Deloitte?", a: "Sim. Nossos modelos de fala e diretrizes acústicas são focados nos critérios usados pelos avaliadores de voz automatizados dessas multinacionais." },
                { q: "Posso ler o eBook no meu celular?", a: "Sim. O arquivo PDF é totalmente adaptável e pode ser lido em celulares, tablets, computadores e leitores Kindle." },
                { q: "Serve para iniciantes?", a: "Sim. Ele começa do básico de escrita e conversação, passando por modelos de fala, micro-ajuste acústico até chegar aos simulados." },
                { q: "Posso solicitar reembolso se não gostar?", a: "Sim, garantimos satisfação total. Se achar que os simulados não ajudaram nos seus estudos, solicite o reembolso em até 7 dias." }
            ]
        },
        ja: {
            metaTitle: "Versantテスト練習用ガイドブック (2026) | 模試20回分＆AI対策ノウハウ",
            metaDesc: "Versantテスト対策のための20の模擬試験、リピートセンテンス回答テンプレート、AI判定キャリブレーション付き。PDF即時ダウンロード可能。",
            boostTag: "🚀 スコアを即座に引き上げる",
            title: "Versant対策完全攻略ガイドブック",
            subtitle: "20回分の模擬試験、回答テンプレート、AI判定対策ノウハウを収録",
            desc: "アルゴリズムに基づいた学習、リピートセンテンスの定型表現、ストーリーリテリングの構成テンプレート、リスニング対策、そして世界中で5,000人以上に支持されている20回分の模擬試験で、Versantスコアを劇的に改善させましょう。",
            features: [
                "リピートセンテンスとストーリーリテリングの合格回答テンプレートを習得。",
                "2026年最新形式に対応した文構築および会話テストの反復練習。",
                "AI採点ソフトが音声を正確に認識するためのマイク位置と音量調整ガイドライン。",
                "20回分の模擬試験の全問題に対する、完璧な模範回答データ。"
            ],
            buyBtn: "ガイドブックを今すぐ入手",
            buyBtnProc: "決済画面へ接続中...",
            regPrice: "通常価格",
            onetime: "一括払い",
            badgeTxt1: "⚡ Razorpayによる安全決済",
            badgeTxt2: "📥 PDF即時ダウンロード可能",
            badgeTxt3: "🔄 無期限アクセス＆無償アップデート",
            badgeTxt4: "🔥 特別セール実施中",
            trustHeader: "5,000人以上の合格者に選ばれています",
            trustDesc: "外資系企業の英語採用試験において、平均スコア68点以上を達成。",
            designedFor: "対応試験・企業:",
            insideTitle: "ガイドブックの収録内容",
            insideDesc: "自動評価AIの採点アルゴリズムを攻略し、採用面接を突破するための完全な設計図を提供します。",
            insideItems: [
                { title: "ストーリーリテリング回答テンプレ", desc: "沈黙を作らず、流暢さの評価スコアを最大化するための正確な文法・文章構成フレームワーク。詳細を忘れても得点できます。", icon: "📝" },
                { title: "AIマイク音響調整ノウハウ", desc: "マイク位置や音量の調整、声のトーン（ピッチ）の制御により、機械の認識エラーによる理不尽な減点を防ぐためのテクニック。", icon: "🎙️" },
                { title: "模試20回分全問の模範解答集", desc: "模試に収録されているスピーキング、リスニング、リーディング、ライティング全パートのGSE基準スコア80（満点レベル）解答例。", icon: "💡" },
                { title: "外資系企業採用フィルター対策", desc: "Amazon、Deloitte、Cognizantなどの採用選考システムが認識しやすい、高頻度の単語、フレーズ、文章構造のチェックリスト。", icon: "🏢" }
            ],
            diffTitle: "このガイドブックが選ばれる理由",
            diffDesc: "ネット上の曖昧なコツや動画とは異なる、アルゴリズムに裏打ちされた体系的な学習システムを比較してください。",
            diffHeaders: ["主な特徴", "ネットの無料情報・YouTube動画", "Versant対策完全攻略ガイドブック"],
            diffRows: [
                { name: "学習マップ", other: "バラバラの動画や矛盾したアドバイス", our: "14日間のステップバイステップ学習ロードマップ" },
                { name: "テンプレート", other: "ストーリーリテリングの使いやすい構成がない", our: "流暢さのスコアを飛躍的に向上させる実証済みのテンプレート" },
                { name: "練習問題", other: "古すぎる問題やネット上の不確かな音声", our: "2026年最新パターンに完全対応した20回分のフル模試" },
                { name: "音響・AI調整", other: "「自信を持って話す」といった曖昧なコツ", our: "マイクの認識バグを回避するための具体的な音響ガイドライン" }
            ],
            previewTitle: "収録内容のサンプルを見る",
            previewDesc: "試験突破に役立つ実際のコンテンツ、戦略、およびテンプレートの一部をご覧ください。以下のタブをクリックするとサンプルページが表示されます。",
            previewTabs: [
                "📄 12ページ: ストーリーリテリング攻略",
                "🎙️ 27ページ: 音声キャリブレーション",
                "💡 45ページ: 高得点模範解答サンプル"
            ],
            ch1Title: "第2章: ストーリーリテリング",
            ch1Sub: "ルール3: AI流暢さ対策用テンプレート",
            ch1P: "自動評価AIは、発話の速度、一時停止（無音状態）の回数、およびリズムを測定します。記憶力の正確さ以上に、いかに自然に淀みなく話せているかが評価されます。以下の文章構成を使用してください。",
            ch1Tip: "💡 対策のコツ: 細部を思い出せなくても、沈黙したり「あー」と声を出したりするよりは、トピックに関連する一般的な類義語を挟んで話し続けましょう。AIはためらいを大幅減点します！",
            ch2Title: "第4章: 音響調整と録音対策",
            ch2Sub: "秘訣7: AI音声周波数チューニング",
            ch2P: "多くの受験者が低スコアになるのは、英語が下手なからではなく、マイクに入力される音響信号が乱れているためです。音声評価ソフトは声の周波数スペクトルを分析して音素を解読します。",
            ch2MicTitle: "🎙️ マイクの正しい位置",
            ch2MicDesc: "マイクを口の真正面に配置しないでください。強い息遣い（PやBの破裂音など）が周波数曲線を歪ませてしまいます。マイクは顎のラインまたは鼻の高さから指2本分外した位置にキープしてください。",
            ch2PitchTitle: "🗣️ 声のトーンと周波数",
            ch2PitchDesc: "声のピッチ（高低）は極力一定に保ってください。高低の変動が大きすぎると、AIが周囲のノイズと区別できなくなります。110Hz〜160Hz of 安定した周波数帯で話すよう心がけてください。",
            ch2Tip: "💡 対策のコツ: ゆっくりと、しかし確信を持って話してください。早口すぎると語の境界が曖昧になり、採点ソフトは文法ミスとしてカウントします。",
            ch3Title: "第6章: 模擬試験の解答解説",
            ch3Sub: "模擬試験パートF: 文章構築（語句整序）（スコア80点満点レベル）",
            ch3P: "以下は模擬試験セット4からの出題例と、語彙、文法、およびスピーキングの流れのすべての評価基準を満たした模範解答です。",
            ch3QTitle: "提示語句 / 文章構築タスク:",
            ch3ATitle: "正解の模範解答例 (Score 80 GSE):",
            ch3Tip: "💡 対策のコツ: 文章構築では、まず主語と動詞の一致に注意しましょう。採点ソフトは、意味的な内容よりも文法的に正しい構造かどうかを第一に追跡します。",
            footerBar: "Versant対策完全攻略ガイドブック",
            secPreview: "安全なPDFプレビュー",
            reviewsTitle: "受講生からの推薦の声",
            reviews: [
                { name: "Harish M.", score: "受験失敗を経て、ガイド使用後に71点を達成", review: "このPDFにあるストーリーリテリングのテンプレートは間違いなく最高です。以前は無駄なつなぎ言葉（フィラー）を多く使っていましたが、ガイドに従うだけでAmazonの採用面接を簡単に突破できました。" },
                { name: "Pooja Hegde", score: "Deloitteの採用基準を超える69点を達成", review: "音響ガイドのおかげでマイク位置や声のコントロール方法が分かりました。音声をうまく認識されずに困っている方は、絶対に読むべきです。" },
                { name: "Vikram Sen", score: "4日間の集中学習で合格基準を突破", review: "無駄な解説がなく、非常にシンプルで分かりやすいです。文構築の模範解答集のおかげで本番で75点を取得できました。精神的にもとても救われました。" }
            ],
            compTitle: "総合英語面接選考対策リソース",
            compDesc: "外資系企業の採用プロセスに合格するために必要な主要知識をお届けします。",
            compItems: [
                { title: "Amazon Versant対策", desc: "Amazonのカスタマーサポートやオペレーション、技術職の採用試験で使用されます。ガイドでは自動選考フィルターをクリアするための単語や回答パターンを網羅しています。" },
                { title: "Deloitte音声選考ハック", desc: "デロイトの音声選考では、文法、流暢さ、発音の高得点が必須です。当ガイドは基準クリアのための音声ピッチや発音矯正テクニックを提供します。" },
                { title: "音声AI採点アルゴリズム", desc: "ピアソンの自動採点エンジンがどのように音声を処理するかを学びます。採点バグを回避して音声を正しく機械に認識させるための解説です。" }
            ],
            faqTitle: "よくあるご質問",
            faqSub: "ガイドブックの購入および内容に関するQ&A",
            faqs: [
                { q: "本はどのように受け取れますか？", a: "決済完了後、即座にサンクスページ（購入完了画面）に戻り、そこから安全なPDFのダウンロードボタンをご利用いただけます。また、その画面でパスワードを設定すれば、ご自身のダッシュボードに永久に保存できます。" },
                { q: "2026年中にテスト形式が変更された場合はアップデートされますか？", a: "はい。本ガイドブックの将来のすべての新版およびボキャブラリー改訂版は、完全無料で提供されます。いつでもご自身のダッシュボードから最新版を再ダウンロード可能です。" },
                { q: "AmazonやDeloitteの合格基準に役立ちますか？", a: "はい。テンプレートとスピーキングラインは、企業の自動音声スクリーニング基準の評価項目をしっかりとカバーするように設計されているため、合格に直接役立ちます。" },
                { q: "スマートフォンで読むことはできますか？", a: "はい。PDFファイルはモバイル、タブレット、ノートPC、Kindleリーダーに最適化されたレイアウトで作成されています。" },
                { q: "初心者でも理解できますか？", a: "はい。アルファベットのつづりや発音の基本から始まり、回答テンプレート、音響調整、模擬試験へと段階的に進むため、どなたでも安心して学習いただけます。" },
                { q: "満足できない場合は返金されますか？", a: "はい。100%満足保証を提供しています。模擬試験がお役に立たなかったと感じた場合は、購入から7日以内であれば全額返金を申請できます。" }
            ]
        }
    };

    const t = translations[locale] || translations.en;

    const getEbookPrice = () => {
        if (currency === 'EUR') return 299; // €2.99
        if (currency === 'USD') return 299; // $2.99
        return 199; // ₹199
    };

    const getEbookPriceString = () => {
        if (currency === 'EUR') return '€2.99';
        if (currency === 'USD') return '$2.99';
        return '₹199';
    };

    const getRegularPriceString = () => {
        if (currency === 'EUR') return '€7.99';
        if (currency === 'USD') return '$7.99';
        return '₹499';
    };

    useEffect(() => {
        trackGA4Event('ebook_page_view');
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUser(currentUser);
        });

        const getDetectedCurrency = () => {
            const country = sessionStorage.getItem('ga4_user_country');
            const browserLang = navigator.language || '';
            const sessionLocale = sessionStorage.getItem('versant_locale');
            if (window.location.pathname.startsWith('/es') || country === 'Spain' || browserLang.startsWith('es') || sessionLocale === 'es') {
                return 'EUR';
            }
            const highPayingUSDCounteries = ['United States', 'United Kingdom', 'Canada', 'Japan', 'Singapore', 'Australia', 'Brazil', 'South Korea'];
            if (highPayingUSDCounteries.includes(country)) {
                return 'USD';
            }
            if (country === 'India') {
                return 'INR';
            }
            return 'USD';
        };
        setCurrency(getDetectedCurrency());

        const handleScroll = () => {
            if (window.scrollY > 500) {
                setShowStickyFooter(true);
            } else {
                setShowStickyFooter(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            unsubscribe();
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Load Razorpay SDK
    const loadRazorpay = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    // Purchase Trigger
    const handleBuyNowClick = () => {
        trackGA4Event('ebook_buy_click', { product_name: 'Versant Test Mastery Ebook', payment_amount: getEbookPrice(), currency: currency });
        if (user) {
            // Already logged in - checkout immediately
            initiateCheckout(user.email, user.uid);
        } else {
            // Guest checkout - launch Razorpay directly
            initiateCheckout('', 'guest');
        }
    };

    const initiateCheckout = async (checkoutEmail, checkoutUid) => {
        setIsProcessing(true);
        const razorpayLoaded = await loadRazorpay();

        if (!razorpayLoaded) {
            alert('Razorpay failed to load. Please check your internet connection.');
            setIsProcessing(false);
            return;
        }

        try {
            const orderResponse = await fetch('/.netlify/functions/createRazorpayOrder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: checkoutUid,
                    email: checkoutEmail,
                    productType: 'ebook',
                    currency: currency,
                    referredBy: localStorage.getItem('versant_affiliate_ref') || null,
                    gaClientId: getGAClientId()
                })
            });

            if (!orderResponse.ok) {
                throw new Error("Failed to create checkout order on server.");
            }

            const orderData = await orderResponse.json();

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Versant Test Mastery Ebook",
                description: "Complete preparation guide with 20 practice sets",
                image: "https://versantpro.com/logo.png",
                order_id: orderData.order_id,
                prefill: {
                    email: (checkoutEmail && checkoutEmail !== 'unknown_email') ? checkoutEmail : '',
                    name: user ? (user.displayName || '') : ''
                },
                theme: {
                    color: "#0f766e" // Teal theme
                },
                modal: {
                    ondismiss: function() {
                        trackGA4Event('razorpay_payment_failed', { product_name: 'Versant Test Mastery Ebook', payment_amount: getEbookPrice(), currency: currency, reason: 'User dismissed checkout modal' });
                        trackFunnelEvent('payment_failed', { product_name: 'Versant Test Mastery Ebook', reason: 'User dismissed checkout modal' });
                    }
                },
                handler: async function (response) {
                    setIsProcessing(true);
                    console.log("Payment Success! Verifying...");
                    try {
                        const verifyRes = await fetch('/.netlify/functions/verifyRazorpayPayment', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                uid: checkoutUid,
                                email: checkoutEmail,
                                productType: 'ebook',
                                referredBy: localStorage.getItem('versant_affiliate_ref') || null
                            })
                        });

                        if (verifyRes.ok) {
                            trackGA4Event('razorpay_payment_success', {
                                transaction_id: response.razorpay_payment_id,
                                payment_amount: getEbookPrice(),
                                currency: currency,
                                product_name: 'Versant Test Mastery Ebook',
                                payment_method: 'razorpay'
                            });
                            // Track purchase in analytics if tracking is set up
                            if (typeof window !== "undefined" && typeof window.gtag === "function") {
                                window.gtag('event', 'purchase_ebook', {
                                    'value': getEbookPrice(),
                                    'currency': currency,
                                    'transaction_id': response.razorpay_payment_id
                                });
                            }
                            trackFunnelEvent('payment_success', {
                                value: getEbookPrice(),
                                currency: currency,
                                product_name: 'Versant Test Mastery Ebook',
                                transaction_id: response.razorpay_payment_id
                            });
                            trackFunnelEvent('ebook_downloaded');
                            // Redirect user directly to Thank You page
                            navigate(`/thank-you?payment_id=${response.razorpay_payment_id}`);
                        } else {
                            let errMsg = "Unknown server error.";
                            try {
                                const errorData = await verifyRes.json();
                                errMsg = errorData.error || errMsg;
                            } catch (e) {
                                errMsg = `Server returned status code ${verifyRes.status}`;
                            }
                            trackFunnelEvent('payment_failed', { product_name: 'Versant Test Mastery Ebook', reason: errMsg });
                            alert("Verification error: " + errMsg);
                        }
                    } catch (verifyError) {
                        console.error("Signature verification query failed:", verifyError);
                        alert("Verification check failed. If amount was deducted, please check your email or contact support.");
                    } finally {
                        setIsProcessing(false);
                    }
                }
            };

            trackGA4Event('razorpay_checkout_open', { product_name: 'Versant Test Mastery Ebook', payment_amount: getEbookPrice(), currency: currency });
            trackFunnelEvent('checkout_started', { product_name: 'Versant Test Mastery Ebook', payment_amount: getEbookPrice(), currency: currency });
            const paymentObject = new window.Razorpay(options);
            paymentObject.open();

        } catch (err) {
            console.error("Payment initialization failed:", err);
            alert("Could not initialize transaction. Please try again later.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 bg-gradient-to-tr from-emerald-50/20 via-gray-50 to-teal-50/20 text-gray-800 font-sans selection:bg-emerald-500/30 overflow-auto">
            <Helmet>
                <title>{t.metaTitle}</title>
                <meta name="description" content={t.metaDesc} />
            </Helmet>

            <Header />

            {/* Ebook Details / Hero Section */}
            <main className="relative py-20 px-6 max-w-7xl mx-auto z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    
                    {/* Left: Text & Features (7 cols) */}
                    <div className="lg:col-span-7 space-y-8 text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full text-emerald-700 text-xs font-bold uppercase tracking-wider">
                            {t.boostTag}
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black text-gray-900 leading-tight tracking-tight">
                            {t.title} <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-650 to-indigo-600 text-2xl md:text-3xl block mt-2 font-extrabold">
                                {t.subtitle}
                            </span>
                        </h1>
                        <p className="text-lg text-gray-650 leading-relaxed font-light">
                            {t.desc}
                        </p>

                        <div className="space-y-4">
                            {t.features.map((feat, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <FaCheckCircle className="text-emerald-500 text-xl mt-1 flex-shrink-0" />
                                    <span className="text-gray-700 font-semibold text-base">{feat}</span>
                                </div>
                            ))}
                        </div>

                        <div className="space-y-3 pt-2">
                            <div className="flex flex-col sm:flex-row items-center gap-6">
                                <button
                                    onClick={handleBuyNowClick}
                                    disabled={isProcessing}
                                    className="w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black rounded-2xl shadow-xl shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600 hover:scale-[1.02] hover:shadow-emerald-400/40 transition-all active:scale-95 text-lg flex items-center justify-center gap-3 disabled:opacity-50"
                                >
                                    {isProcessing ? t.buyBtnProc : `${t.buyBtn} – ${getEbookPriceString()}`} 
                                    <FaArrowRight />
                                </button>
                                <div className="text-left">
                                    <div className="text-gray-400 text-xs line-through">{t.regPrice} {getRegularPriceString()}</div>
                                    <div className="text-2xl font-black text-gray-900">{getEbookPriceString()} <span className="text-xs text-emerald-600 font-bold uppercase tracking-wider">{t.onetime}</span></div>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 font-medium flex flex-wrap gap-x-4 gap-y-1">
                                <span>{t.badgeTxt1}</span>
                                <span>• {t.badgeTxt2}</span>
                                <span>• {t.badgeTxt3}</span>
                                <span className="text-emerald-700 font-bold">{t.badgeTxt4}</span>
                            </p>
                        </div>
                    </div>

                    {/* Right: Premium Ebook Cover Image (5 cols) */}
                    <div className="lg:col-span-5 flex justify-center relative">
                        <div className="absolute inset-0 bg-emerald-500/10 blur-[80px] rounded-full mix-blend-screen pointer-events-none"></div>
                        <img 
                            src="/images/versant_ebook_cover.jpg" 
                            alt="Versant Test Mastery Ebook Cover" 
                            className="w-80 h-auto rounded-3xl shadow-2xl border border-gray-200 hover:scale-[1.02] transition-transform duration-300 relative z-10 select-none"
                        />
                    </div>
                </div>

                {/* Trust Badge Strip */}
                <div className="mt-16 bg-white border border-gray-150 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
                    <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className="flex text-yellow-400 text-lg">
                            <FaStar/><FaStar/><FaStar/><FaStar/><FaStar/>
                        </div>
                        <div>
                            <p className="text-gray-900 font-extrabold text-base">{t.trustHeader}</p>
                            <p className="text-gray-505 text-xs mt-0.5">{t.trustDesc}</p>
                        </div>
                    </div>
                    <div className="hidden md:block h-12 w-px bg-gray-200"></div>
                    <div className="flex flex-wrap justify-center gap-3 items-center">
                        <span className="text-gray-500 text-xs font-bold uppercase tracking-wider mr-2">{t.designedFor}</span>
                        {["Amazon", "Concentrix", "Genpact", "Teleperformance", "Deloitte", "Cognizant"].map((company, index) => (
                            <span key={index} className="px-3 py-1.5 bg-gray-50 border border-gray-205 text-gray-700 text-xs font-black rounded-lg shadow-sm">
                                {company}
                            </span>
                        ))}
                    </div>
                </div>

                {/* What's Inside Section */}
                <div className="mt-28 border-t border-gray-200/80 pt-16">
                    <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-4">{t.insideTitle}</h2>
                    <p className="text-gray-500 text-center max-w-xl mx-auto mb-16 text-sm sm:text-base">{t.insideDesc}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {t.insideItems.map((item, idx) => (
                            <div key={idx} className="bg-white border border-gray-100 p-6 rounded-3xl text-left hover:-translate-y-1.5 transition-all duration-300 shadow-sm hover:shadow-lg group flex flex-col justify-between">
                                <div>
                                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 text-2xl mb-6 shadow-inner group-hover:scale-110 transition-transform">{item.icon}</div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm font-light leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Value Comparison Table */}
                <div className="mt-28 border-t border-gray-200/80 pt-16">
                    <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-4">{t.diffTitle}</h2>
                    <p className="text-gray-500 text-center max-w-xl mx-auto mb-12 text-sm sm:text-base">{t.diffDesc}</p>
                    
                    <div className="max-w-4xl mx-auto bg-white border border-gray-150 rounded-3xl overflow-hidden shadow-lg">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-150">
                                        <th className="p-5 font-black text-gray-900 text-base">{t.diffHeaders[0]}</th>
                                        <th className="p-5 font-black text-red-650 bg-red-50/20 text-center">{t.diffHeaders[1]}</th>
                                        <th className="p-5 font-black text-emerald-700 bg-emerald-50/20 text-center">{t.diffHeaders[2]}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {t.diffRows.map((row, index) => (
                                        <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="p-5 font-bold text-gray-900">{row.name}</td>
                                            <td className="p-5 text-gray-500 bg-red-50/5 text-center">{row.other}</td>
                                            <td className="p-5 text-emerald-950 font-bold bg-emerald-50/5 text-center flex items-center justify-center gap-2">
                                                <FaCheckCircle className="text-emerald-500 text-base flex-shrink-0" />
                                                {row.our}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Ebook Previewer / Sneak Peek Section */}
                <div className="mt-28 border-t border-gray-200/80 pt-16">
                    <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-4">{t.previewTitle}</h2>
                    <p className="text-gray-500 text-center max-w-xl mx-auto mb-10 text-sm sm:text-base">
                        {t.previewDesc}
                    </p>

                    <div className="max-w-4xl mx-auto bg-white border border-gray-150 rounded-3xl p-6 sm:p-10 shadow-xl relative">
                        {/* Tab buttons */}
                        <div className="flex flex-wrap gap-2 justify-center mb-8 border-b border-gray-200/80 pb-6">
                            {[
                                { label: t.previewTabs[0], id: 0 },
                                { label: t.previewTabs[1], id: 1 },
                                { label: t.previewTabs[2], id: 2 }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActivePreviewTab(tab.id);
                                        trackGA4Event('ebook_preview_click', { page_number: tab.id === 0 ? 12 : tab.id === 1 ? 27 : 45, tab_label: tab.label });
                                    }}
                                    className={`px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                                        activePreviewTab === tab.id
                                            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                                            : 'bg-gray-100 text-gray-600 border border-gray-250 hover:text-gray-900'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Page mockup container */}
                        <div className="bg-gray-55 border border-gray-200 rounded-2xl p-6 sm:p-10 shadow-inner relative overflow-hidden text-left min-h-[400px] flex flex-col justify-between">
                            {/* Watermark */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-12 text-gray-300/40 text-4xl sm:text-6xl font-black select-none pointer-events-none tracking-widest z-0 uppercase">
                                Versant Mastery Preview
                            </div>

                            <div className="relative z-10 space-y-6">
                                {activePreviewTab === 0 && (
                                    <>
                                        <div className="flex justify-between items-center text-xs text-emerald-700 font-mono tracking-wider border-b border-gray-200 pb-3">
                                            <span>{t.ch1Title}</span>
                                            <span>PAGE 12</span>
                                        </div>
                                        <h3 className="text-xl font-black text-gray-900">{t.ch1Sub}</h3>
                                        <p className="text-gray-700 text-sm leading-relaxed font-light">
                                            {t.ch1P}
                                        </p>
                                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 font-mono text-xs sm:text-sm text-emerald-400 space-y-3 shadow-inner">
                                            <p className="border-l-2 border-emerald-500 pl-3">
                                                "The speaker discussed <span className="text-slate-450">[Main Topic/Character]</span>. 
                                                First, they pointed out that <span className="text-slate-450">[Key Detail 1]</span>."
                                            </p>
                                            <p className="border-l-2 border-emerald-500 pl-3">
                                                "Next, they emphasized that <span className="text-slate-450">[Key Detail 2]</span>. 
                                                Additionally, it was stated that <span className="text-slate-450">[Key Detail 3]</span>."
                                            </p>
                                            <p className="border-l-2 border-emerald-500 pl-3">
                                                "In addition, they highlighted the importance of <span className="text-slate-450">[Key Detail 4]</span>. 
                                                Overall, the speech was about <span className="text-slate-450">[Concluding thought]</span>."
                                            </p>
                                        </div>
                                        <p className="text-xs text-gray-500 italic">
                                            {t.ch1Tip}
                                        </p>
                                    </>
                                )}

                                {activePreviewTab === 1 && (
                                    <>
                                        <div className="flex justify-between items-center text-xs text-emerald-700 font-mono tracking-wider border-b border-gray-200 pb-3">
                                            <span>{t.ch2Title}</span>
                                            <span>PAGE 27</span>
                                        </div>
                                        <h3 className="text-xl font-black text-gray-900">{t.ch2Sub}</h3>
                                        <p className="text-gray-700 text-sm leading-relaxed font-light">
                                            {t.ch2P}
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="bg-slate-905 border border-gray-200 rounded-xl p-4 text-xs space-y-2">
                                                <span className="text-emerald-600 font-bold uppercase tracking-wider text-[10px]">{t.ch2MicTitle}</span>
                                                <p className="text-gray-705">
                                                    {t.ch2MicDesc}
                                                </p>
                                            </div>
                                            <div className="bg-slate-905 border border-gray-200 rounded-xl p-4 text-xs space-y-2">
                                                <span className="text-emerald-600 font-bold uppercase tracking-wider text-[10px]">{t.ch2PitchTitle}</span>
                                                <p className="text-gray-705">
                                                    {t.ch2PitchDesc}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 italic">
                                            {t.ch2Tip}
                                        </p>
                                    </>
                                )}

                                {activePreviewTab === 2 && (
                                    <>
                                        <div className="flex justify-between items-center text-xs text-emerald-700 font-mono tracking-wider border-b border-gray-200 pb-3">
                                            <span>{t.ch3Title}</span>
                                            <span>PAGE 45</span>
                                        </div>
                                        <h3 className="text-xl font-black text-gray-900">{t.ch3Sub}</h3>
                                        <p className="text-gray-700 text-sm leading-relaxed font-light">
                                            {t.ch3P}
                                        </p>
                                        <div className="space-y-3">
                                            <div className="bg-slate-900 border border-slate-800 p-4 rounded-xl text-xs space-y-1.5">
                                                <div className="text-slate-400 font-bold uppercase text-[9px]">{t.ch3QTitle}</div>
                                                <p className="text-slate-300 italic">"leaves / during / trees / deciduous / lose / winter / their"</p>
                                            </div>
                                            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl text-xs space-y-1.5">
                                                <div className="text-emerald-750 font-bold uppercase text-[9px] flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                                    {t.ch3ATitle}
                                                </div>
                                                <p className="text-emerald-900 font-mono font-medium text-sm">"Deciduous trees lose their leaves during winter."</p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 italic">
                                            {t.ch3Tip}
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="border-t border-gray-200 mt-6 pt-4 flex justify-between items-center text-xs text-gray-400">
                                <span>{t.footerBar}</span>
                                <span>{t.secPreview}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reviews Section */}
                <div className="mt-28 border-t border-gray-200/80 pt-16">
                    <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">{t.reviewsTitle}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {t.reviews.map((rev, idx) => (
                            <div key={idx} className="bg-white border border-gray-100 p-8 rounded-3xl text-left shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex text-yellow-400 mb-4"><FaStar/><FaStar/><FaStar/><FaStar/><FaStar/></div>
                                <p className="text-gray-650 italic mb-6">"{rev.review}"</p>
                                <div className="font-bold text-gray-900 text-sm">{rev.name}</div>
                                <div className="text-xs text-emerald-600 font-mono mt-1">{rev.score}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* SEO Keyword Prep Hub Section */}
                <div className="mt-28 border-t border-gray-200/80 pt-16">
                    <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-4">{t.compTitle}</h2>
                    <p className="text-gray-500 text-center max-w-xl mx-auto mb-16 text-sm sm:text-base">
                        {t.compDesc}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                        {t.compItems.map((item, idx) => (
                            <div key={idx} className="bg-white border border-gray-150 p-6 rounded-3xl space-y-4 shadow-sm hover:shadow-md transition-shadow">
                                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    <span className="text-emerald-600">🏢</span> {item.title}
                                </h3>
                                <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* FAQs Section */}
                <div className="mt-28 border-t border-gray-200/80 pt-16 pb-12">
                    <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-4">{t.faqTitle}</h2>
                    <p className="text-gray-500 text-center max-w-xl mx-auto mb-16 text-sm">{t.faqSub}</p>
                    
                    <div className="max-w-3xl mx-auto space-y-4 text-left">
                        {t.faqs.map((faq, idx) => (
                            <FAQItem key={idx} question={faq.q} answer={faq.a} />
                        ))}
                    </div>
                </div>
            </main>

            {/* Background elements */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-200/20 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-teal-200/10 blur-[120px] rounded-full"></div>
            </div>

            {/* Sticky Mobile Buy Button Bar */}
            <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-250 p-4 z-50 flex items-center justify-between transition-all duration-300 md:hidden shadow-[0_-4px_20px_rgba(0,0,0,0.08)] ${
                showStickyFooter ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
            }`}>
                <div>
                    <span className="text-xs text-gray-400 line-through">{getRegularPriceString()}</span>
                    <div className="text-lg font-black text-gray-950">{getEbookPriceString()}</div>
                </div>
                <button
                    onClick={handleBuyNowClick}
                    disabled={isProcessing}
                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-sm rounded-xl shadow-md disabled:opacity-50 flex items-center gap-2"
                >
                    {isProcessing ? "Processing..." : t.buyBtn} <FaArrowRight className="text-xs" />
                </button>
            </div>

            <Footer />
        </div>
    );
}

function FAQItem({ question, answer }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="bg-white border border-gray-150 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-6 flex justify-between items-center text-left text-gray-900 font-bold text-base hover:bg-gray-50 transition-colors focus:outline-none"
            >
                <span>{question}</span>
                <span className={`text-xl transition-transform duration-300 ${isOpen ? 'rotate-180 text-emerald-600' : 'text-gray-400'}`}>
                    {isOpen ? '−' : '+'}
                </span>
            </button>
            <div className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-48 border-t border-gray-100 p-6 bg-gray-50/50' : 'max-h-0'}`}>
                <p className="text-gray-600 text-sm font-light leading-relaxed">{answer}</p>
            </div>
        </div>
    );
}
